import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import VoiceoverBottomBar from "./VoiceoverBottomBar";
import { useCallback, useEffect, useState } from "react";
import Toast from "@/lib/components/Toast";
import { Button } from "@/lib/components/Button/Button";
import ScriptEditor from "./ScriptEditor";
import { getVoiceOverOverflowMessage } from "./voiceover-overflow";
import { parseHtmlToPlainText } from "@/lib/utils";
import { VoiceOverDto } from "@repo/api/editor";
import VoiceActorDialog from "./VoiceActorDialog";
import { z } from "zod/v4";

const voiceOverFormSchema = z.object({
  script: z.string().check(
    z.refine((val) => !!parseHtmlToPlainText(val), {
      message: "Script is required",
    }),
  ),
  voiceActorExternalId: z
    .string({ error: "Voice actor is required" })
    .nullable()
    .check(
      z.refine((val) => val !== null, {
        message: "Voice actor is required",
      }),
    ),
});

type VoiceOverFormErrors = {
  script?: string;
  voiceActorExternalId?: string;
};

function CreateVoiceoverTab() {
  const { script, setScript, handleSubmit, isLoading, errors } =
    useVoiceOverForm();

  return (
    <div className="relative flex h-full flex-col">
      <div className="grow overflow-y-auto px-6 pt-6 pb-8">
        <ScriptEditor
          script={script}
          setScript={setScript}
          error={errors.script}
        />
        <div className="mt-1.5 flex w-full flex-col justify-between gap-10">
          <div className="flex w-full flex-row items-end gap-5">
            <div className="w-1/2">
              <VoiceActorDialog error={errors.voiceActorExternalId} />
            </div>
            <div className="flex w-1/2 flex-col">
              <Button onClick={handleSubmit} className="w-full">
                {isLoading ? "Generating voiceover" : "Generate voiceover"}
              </Button>
              <p className="invisible mt-1.5 text-sm" aria-hidden="true">
                {"\u00A0"}
              </p>
            </div>
          </div>
        </div>
      </div>
      <VoiceoverBottomBar isLoading={isLoading} />
    </div>
  );
}

function useVoiceOverForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<VoiceOverFormErrors>({});
  const { script, voiceActorExternalId } = useEditorStore(
    (state) => state.voiceOverForm,
  );
  const updateVoiceOverForm = useEditorStore(
    (state) => state.service.updateVoiceOverForm,
  );

  useEffect(() => {
    if (voiceActorExternalId) {
      setErrors((prev) => ({ ...prev, voiceActorExternalId: undefined }));
    }
  }, [voiceActorExternalId]);

  const setScript = useCallback(
    (script: string) => {
      updateVoiceOverForm((form) => {
        setErrors((prev) => ({ ...prev, script: undefined }));
        form.script = script;
      });
    },
    [updateVoiceOverForm],
  );

  const setVoiceActorExternalId = useCallback(
    (voiceActorExternalId: string | null) => {
      updateVoiceOverForm((form) => {
        form.voiceActorExternalId = voiceActorExternalId;
      });
    },
    [updateVoiceOverForm],
  );

  const createVoiceOver = useEditorStore(
    (state) => state.service.createVoiceOver,
  );
  const getVoiceOverOverflowMs = useEditorStore(
    (state) => state.service.getVoiceOverOverflowMs,
  );

  const clearVoiceOver = useEditorStore(
    (state) => state.service.clearVoiceOver,
  );

  const handleSubmit = useCallback(async () => {
    const result = z.safeParse(voiceOverFormSchema, {
      script,
      voiceActorExternalId,
    });

    if (!result.success) {
      const fieldErrors: VoiceOverFormErrors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof VoiceOverFormErrors;
        if (!fieldErrors[key]) {
          fieldErrors[key] = issue.message;
        }
      }
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    setIsLoading(true);

    let voiceOver: VoiceOverDto;

    try {
      voiceOver = await createVoiceOver(script, voiceActorExternalId!);
    } catch (err) {
      console.error(err);
      Toast.error("Failed to create voice-over");
      return;
    } finally {
      setIsLoading(false);
    }

    const overflowMs = getVoiceOverOverflowMs(voiceOver.durationMs);

    if (overflowMs > 0) {
      Toast.error(getVoiceOverOverflowMessage(overflowMs), { duration: 6000 });
    }
  }, [script, voiceActorExternalId, createVoiceOver, getVoiceOverOverflowMs]);

  const handleClear = useCallback(() => {
    clearVoiceOver();
    setScript("");
    setVoiceActorExternalId(null);
    setErrors({});
  }, [clearVoiceOver, setScript, setVoiceActorExternalId]);

  return {
    script,
    setScript,
    voiceActorExternalId,
    setVoiceActorExternalId,
    handleSubmit,
    isLoading,
    errors,
    handleClear,
  };
}

export default CreateVoiceoverTab;
